import React, {useContext, useEffect, useRef, useState} from 'react';
import {ControlPosition, MapControl} from "@vis.gl/react-google-maps";
import {FaAngleLeft, FaAngleRight} from "react-icons/fa";
import {v4 as uuidv4} from "uuid";

import {AreaLayerContext} from "./area-layer-context.jsx";

import InputForm from "../../../../components/input/input-form.jsx";
import PanelDefault from "./components/panel-default.jsx";
import PanelAreaDetails from "./components/panel-area-details.jsx";
import ImportKmzKml from "./components/import-kmz-kml.jsx";
import AreasList from "./components/areas-list.jsx";
import {usePolygons} from "./hooks/use-polygons.jsx";
import Button from "../../../../components/button/button.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import PanelKmz from "./components/panel-kmz.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import Swal from "sweetalert2";
import areaLayerService from "../../../../services/web/area-layer-service.js";

const API_URL = import.meta.env.VITE_API_URL;

const AreaLayerForm = () => {
    const navigate = navigateHook();
    const [nameLayer, setNameLayer] = useState("");

    const [showMenuPanel, setShowMenuPanel] = useState(true);
    const [showAreaDetailsPanel, setShowAreaDetailsPanel] = useState(false);
    const [showKmzPanel, setShowKmzPanel] = useState(false);
    const [showLayerListPanel, setShowLayerListPanel] = useState(true);
    const [itemsToRemove, setItemsToRemove] = useState([])

    const [kmzData, setKmzData] = useState([])
    const [areaSelected, setAreaSelected] = useState();

    const {
        map, setIsLoading,
        areas, setAreas,
        markerLib,
        coreLib, id, mapLib, position
    } = useContext(AreaLayerContext);

    const areasRef = useRef(areas);
    areasRef.current = areas;

    const {createPolygon, removeEditableDraggable, removePolygon} = usePolygons({
        map, setShowAreaDetailsPanel,
        mapLib,
        coreLib, areasRef, setAreaSelected
    });

    useEffect(() => {
        if (!map || !coreLib || !markerLib) return;

        map.setOptions({
            styles: [
                {"elementType": 'labels', "stylers": [{"visibility": 'ON'}]},
                {"featureType": "poi", "elementType": "all", "stylers": [{"visibility": 'off'}]},
                {"featureType": "road", "elementType": "labels.text.fill", "stylers": [{"visibility": 'on'}]},
            ]
        });
    }, [map, coreLib, markerLib]);

    useEffect(() => {
        if (!showMenuPanel) {
            setShowKmzPanel(false)
            setShowAreaDetailsPanel(false)
            setShowAreaDetailsPanel(false)
        }
    }, [showMenuPanel]);

    useEffect(() => {
        if (showAreaDetailsPanel) {
            setShowMenuPanel(true)
        } else {
            removeEditableDraggable(areaSelected)
            setAreaSelected(undefined)
        }
    }, [showAreaDetailsPanel]);

    useEffect(() => {
        if (areaSelected) {
            const bounds = new coreLib.LatLngBounds();
            areaSelected.polygon.latLngs.forEach(ke => {
                ke.forEach(ll => {
                    bounds.extend(new google.maps.LatLng(ll.lat(), ll.lng()));
                });
            });

            map.fitBounds(bounds);
        }
    }, [areaSelected]);

    const {data: areaLayerData, refetch, isFetching, isSuccess} = useQuery({
        queryKey: ['area-layer'],
        queryFn: async () => await areaLayerService.getById(id).then(({data}) => data),
        enabled: !!id
    });

    useEffect(() => {
        if (!isFetching && areaLayerData) {
            const {layer, areas} = areaLayerData
            const newAreas = []
            const bounds = new coreLib.LatLngBounds();
            areas.map(a => {
                const {id, color, description, name, coordinates} = a

                const coordinatesParsed = JSON.parse(coordinates)
                const paths = [];

                for (let i = 0; i < coordinatesParsed.length; i += 2) {
                    paths.push({
                        lat: coordinatesParsed[i],
                        lng: coordinatesParsed[i + 1]
                    });
                }

                for (const path of paths) {
                    bounds.extend(new coreLib.LatLng(path.lat, path.lng));
                }

                const area = createPolygon({
                    id, color, observation: description, name,
                    draggable: false, editable: false, paths
                })

                newAreas.push(area)
            })

            map.fitBounds(bounds);
            setNameLayer(layer.name)
            setAreas(newAreas)
        }
    }, [areaLayerData, isFetching]);

    const addLayer = () => {
        const id = uuidv4();
        const area = createPolygon({id, position})

        setAreas(prev => [...prev, area]);
        setAreaSelected(area);
        removeEditableDraggable(areaSelected)
        setShowAreaDetailsPanel(true)
    }

    const save = () => {
        if (!nameLayer) {
            projectUtils.swalErrorMessage("", "O campo \"Nome\" é obrigatório.", "Ok");
            return;
        }

        setIsLoading(true)

        const areasPrepared = areas.filter(a => a.data.name).map(a => {
            const {data, id, polygon} = a
            const {name, observation, color} = data

            const coordinates = [];
            const vertices = polygon.getPath();
            const coords = [];
            for (let i = 0; i < vertices.getLength(); i++) {
                const xy = vertices.getAt(i);

                coords.push(xy.lat());
                coords.push(xy.lng());
            }
            coordinates.push(coords);

            return {id, color, coordinates, description: observation, name}
        })

        const formData = new FormData();
        formData.append("id", id || "");
        formData.append("name", nameLayer);
        formData.append("removed", JSON.stringify(itemsToRemove));
        formData.append("sectors", JSON.stringify(areasPrepared));

        areaLayerService.save(formData)
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    for (const area of areas) {
                        removePolygon(area?.polygon)
                    }

                    refetch()
                    return;
                }

                const urlDev = `/area-layer/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        const cacheAreas = areas.filter(p => typeof p.id  === 'string')
        setShowLayerListPanel(cacheAreas.length === 0)
    }, [areas]);

    const remove = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o setor "${nameLayer}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                areaLayerService.remove(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");

                        const urlDev = "/area-layer";
                        navigate(urlDev);
                    })
                    .catch(error => {
                        console.error(error);
                        let message = 'Não foi possível excluir.';
                        if (error.response.status !== 500) {
                            message = error.response.data.message;
                        }

                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            }
        });
    }

    return (
        <>
            <MapControl position={ControlPosition.LEFT_TOP}>
                <PanelDefault
                    show={showMenuPanel}
                    setShow={setShowMenuPanel}
                    className={["z-1"]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <InputForm
                        id="name-area"
                        label="Nome *"
                        value={nameLayer}
                        onChange={e => setNameLayer(e.target.value)}
                    />


                    <div className={"mt-2"}>
                        <ImportKmzKml setShowKmzPanel={setShowKmzPanel} setKmzData={setKmzData}/>
                    </div>

                    <div className="mt-2">
                        <div
                            className="mb-3 border border-[#bdbdbd] text-[#063A6C] rounded-md flex justify-between items-center px-2.5 h-8 cursor-pointer hover:bg-[#e7eaec]"
                            onClick={addLayer}
                        >
                            <span className="text-xs font-bold">
                                Adicionar setor
                            </span>

                            {showAreaDetailsPanel ? <FaAngleLeft/> : <FaAngleRight/>}
                        </div>
                    </div>

                    <div className={"mt-3 h-[calc(100vh-266px)] overflow-y-auto overflow-x-hidden mb-3"}>
                        <AreasList
                            cacheList={false}
                            setShowPanel={setShowAreaDetailsPanel}
                            setAreaSelected={setAreaSelected}
                        />
                    </div>

                    <div className={"flex justify-end gap-3"}>
                        {id && <Button target={"danger"} onClick={remove} actionFeedbackType='delete'>Excluir</Button>}
                        <Button onClick={save} actionFeedbackType='save'>Salvar</Button>
                    </div>

                </PanelDefault>

                <PanelDefault
                    show={showAreaDetailsPanel}
                    setShow={setShowAreaDetailsPanel}
                    className={[`${showAreaDetailsPanel ? "left-62" : ""} z-0 p-5 w-80`]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <PanelAreaDetails
                        showPanel={showAreaDetailsPanel}
                        setShowPanel={setShowAreaDetailsPanel}
                        setAreas={setAreas}
                        areaSelected={areaSelected}
                        areas={areas}
                        setItemsToRemove={setItemsToRemove}
                    />
                </PanelDefault>

                <PanelDefault
                    show={showKmzPanel}
                    setShow={setShowKmzPanel}
                    className={[`${showKmzPanel ? "left-62" : ""} z-0 p-5 w-125`]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <PanelKmz
                        kmzData={kmzData}
                        createPolygon={createPolygon}
                        iconSelected={areaSelected}
                        removePolygon={removePolygon}
                        showKmzPanel={showKmzPanel}
                        setShowKmzPanel={setShowKmzPanel}
                        coreLib={coreLib}
                        map={map}
                    />
                </PanelDefault>
            </MapControl>

            <MapControl position={ControlPosition.RIGHT_TOP}>
                <PanelDefault
                    show={showLayerListPanel}
                    setShow={setShowLayerListPanel}
                    className={["!-l-[250px] h-106.75! overflow-auto"]}
                    btnClassName={["hidden"]}
                >
                    <Alert target={"danger"}>
                        <span className={"text-[12px]"}><b>Atenção</b>: As alterações dos setores abaixo ainda não foram aplicadas.</span>
                    </Alert>
                    <AreasList
                        cacheList={true}
                        setShowPanel={setShowAreaDetailsPanel}
                        setAreaSelected={setAreaSelected}
                    />
                </PanelDefault>
            </MapControl>

            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <div className={"absolute -left-47.5 -bottom-31.5"}>
                    <img src={`${API_URL}/public/assets/img/sentry-logo2.png`} alt="image" style={{"width": "120px"}}/>
                </div>
            </MapControl>
        </>
    );
}

export default AreaLayerForm;